import { Feature } from '~/graphql/generated/query.types'

export type FeatureFlagConfig = {
  label: string
  description?: string
  isHidden?: boolean
  forceEnabledForDemoFirms: boolean
  forDemosOnly?: boolean
}

export const featureFlagConfigs: Record<Feature, FeatureFlagConfig> = {
  // Visible flags (flags are still in use)
  [Feature.overviewDashboard]: { label: 'Overview dashboard', forceEnabledForDemoFirms: true },
  [Feature.renewalForecasting]: {
    label: 'Renewal forecasting',
    description:
      'Use renewal predictions model in availability forecast and lease expiration forecasting. If disabled, lease expiration forecasting will use the renewal rate assumption setting.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.reports]: {
    label: 'Reports',
    description:
      'Adds some example custom reports, and shows the scheduled reports tab. Only for demo purposes.',
    forceEnabledForDemoFirms: false,
    forDemosOnly: true,
  },
  // TODO(scott, 2025-09-11): Clean this flag up since we have the firm column
  // shouldGeneratePriceRecommendations
  [Feature.priceRecommendations]: {
    label: 'Price recommendations',
    forceEnabledForDemoFirms: false,
  },
  [Feature.priceRecommendationScenarioView]: {
    label: 'Price recommendation scenario view',
    description:
      'For internal testing of price recommendation scenarios. Do not turn on for clients.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.priceRecommendationFeedback]: {
    label: 'Price recommendation feedback',
    description:
      'For customers to provide feedback on price recommendations. Not really used anymore.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.renewalsScheduledUpdates]: {
    label: 'Renewals scheduled updates',
    description:
      'For demo and internal testing of renewals scheduled updates. Feature is not complete.',
    forceEnabledForDemoFirms: false,
    forDemosOnly: true,
  },
  [Feature.autoPilot]: { label: 'AutoPilot', isHidden: false, forceEnabledForDemoFirms: true },
  [Feature.automaticLeaseExpirationTargets]: {
    label: 'Automatic lease expiration targets',
    description: 'For demo of automatic lease expiration targets. Feature is not complete.',
    forceEnabledForDemoFirms: false,
    forDemosOnly: true,
  },
  [Feature.monthToMonthExpirationForecasting]: {
    label: 'Month-to-month expiration forecasting',
    description:
      'Use month-to-month expiration forecasting in availability forecast and recommendations.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.renewalShortTermPenaltyFactor]: {
    label: 'Renewal short-term penalty factor',
    description: 'Gives option to turn on renewal short-term penalty.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.gradualLeaseExpirationPenaltyControls]: {
    label: 'Gradual lease expiration penalty controls',
    description:
      'Gives option to turn off gradual lease expiration penalty for new leases and renewals.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.upcomingAppointmentsView]: {
    label: 'Upcoming appointments',
    description:
      'Shows the upcoming appointments stat in property stats bar. For Entrata firms only.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.shouldOmitPricesForNewLeaseTermsOverExpirationCap]: {
    label: 'Omit new lease terms over expiration caps (Montgomery County)',
    description:
      'Omit new lease terms over expiration caps in rent matrix calculations, which was requested by certain clients in Montgomery County.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.seasonalBiasCorrection]: {
    label: 'Seasonal bias correction',
    description: 'Gives option to turn on seasonal bias correction rent matrix settings.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.earlyLeaseTerminationForecasting]: {
    label: 'Early lease termination forecasts',
    description:
      'Use early lease termination forecasts in availability forecast and recommendations.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.maxBestTermSetting]: {
    label: 'Maximum best term setting',
    description: 'Gives option to set a maximum best term in rent matrix settings.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.minBestTermSetting]: {
    label: 'Minimum best term setting',
    description:
      'Gives option to set a minimum best term for new leases and renewals in rent matrix settings, making the least-exposed offered term at or above the minimum match the best price for each move-in date.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.distributeGuestcardsByAvailability]: {
    label: 'Distribute guestcards by availability',
    description:
      'Changes guestcard distribution to be based on unit availability, when bed count (or unit type) is not specified.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.overviewPortfolioRollUps]: {
    label: 'Overview portfolio roll-ups',
    description: 'Show portfolio roll-ups on the overview page.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.hideCompPropertiesView]: {
    label: 'Hide comp properties view',
    forceEnabledForDemoFirms: false,
  },
  [Feature.alertsAndInsightsForPortfolioAndFirm]: {
    label: 'Alerts and insights (portfolio and firm)',
    forceEnabledForDemoFirms: false,
  },
  [Feature.editUnitAmenities]: {
    label: 'Edit unit amenities',
    description: 'Gives option to edit unit amenities for demo only.',
    forceEnabledForDemoFirms: false,
    forDemosOnly: true,
  },
  [Feature.rentanaChat]: {
    label: 'Rentana chat',
    description: 'Enable the new Rentana chat experience.',
    forceEnabledForDemoFirms: false,
  },

  // Hidden flags (isHidden: true) that are no longer referenced in the codebase
  [Feature.montgomeryCountyRentControl]: {
    label: 'Montgomery County rent control',
    description:
      'No longer consulted. New-lease rent control settings are always available in property settings, unit-group overrides, and company property settings.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.alertsAndInsights]: {
    label: 'Alerts and insights',
    description: 'No longer consulted. Property alerts and insights are always available.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.propertySummaryEmail]: {
    label: 'Property summary email',
    description:
      'No longer consulted. Property summary email settings are always available in property settings.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.leadSourceTracking]: {
    label: 'Lead source tracking',
    description:
      'No longer consulted. Lead source tracking is always available on the property summary and leads views.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.pricingAnalysis]: {
    label: 'Pricing analysis',
    description:
      'No longer consulted. Pricing analyses are always shown when price recommendations are disabled.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.propertySettingsBulkImport]: {
    label: 'Property settings bulk import',
    description:
      'No longer consulted. Property settings bulk import is always available on the company properties page.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.customUnitGroupPriceRecommendations]: {
    label: 'Custom unit group price recommendations',
    description:
      'No longer consulted. Custom unit group price recommendations (instead of bedroom count) are always available in property settings.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.netApplicationsMetric]: {
    label: 'Net applications metric',
    description:
      'No longer consulted. Net applications always uses completed applications minus cancellations minus denials.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.combinedProperties]: {
    label: 'Combined properties',
    description: 'Enables the combined properties feature.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.marketRentAdjustmentsInPricingReview]: {
    label: 'Market rent adjustments in pricing review',
    description:
      'No longer consulted. Market rent adjustments and suggestions always appear in the pricing review modal.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.unitRentControlOverrides]: {
    label: 'Unit rent control overrides',
    description:
      'No longer consulted. Unit-level rent control overrides are always available on the units list and unit summary.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.unitGroupPropertySettingOverrides]: {
    label: 'Unit group property setting overrides',
    isHidden: true,
    forceEnabledForDemoFirms: false,
  },
  [Feature.budgetedRentTracking]: {
    label: 'Budgeted rent tracking',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.pricingReviewV2]: {
    label: 'Pricing review v2',
    isHidden: true,
    forceEnabledForDemoFirms: false,
  },
  [Feature.priceRecommendationDemo]: {
    label: 'Price recommendation demo',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.renewals]: { label: 'Renewals', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.metricsPage]: { label: 'Metrics page', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.propertyPricingSummaryEmail]: {
    label: 'Property pricing summary email',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.newEmail]: { label: 'New email', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.lossToLease]: { label: 'Loss to lease', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.customUnitGroupings]: {
    label: 'Custom unit groupings',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.specials]: { label: 'Specials', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.amenities]: { label: 'Amenities', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.renewalsSummaryView]: {
    label: 'Renewals summary view',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.priceRecommendationsPage]: {
    label: 'Price history page',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.createMultipleRenewalsBatches]: {
    label: 'Create multiple renewals batches',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.alertsPage]: { label: 'Alerts page', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.priceHistoryV2]: {
    label: 'Price history v2',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.renewalsMonthToMonthView]: {
    label: 'Renewals month-to-month view',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.priceRecommendationsUnitAvailability]: {
    label: 'Unit availability for recommendations',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.occupancyTargetOverrides]: {
    label: 'Occupancy target overrides',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.leaseTradeOutReports]: {
    label: 'Lease trade-out reports',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.navRedesign]: { label: 'Nav redesign', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.marketRentAdjustment]: {
    label: 'Market rent adjustment',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.recipientGroups]: {
    label: 'Recipient groups',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.priceRecommendationsCompLowerLimit]: {
    label: 'Comps-based lower limit for recommendations',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.exposeCompressedPriceRecommendation]: {
    label: 'Expose pre-limits price recommendation in user-facing explanation',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.recentAchievedAverageMarketRent]: {
    label: 'Recent achieved average market rent',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.executedLeasesReport]: {
    label: 'Executed leases report',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.offerAnalysis]: {
    label: 'Offer analysis',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.leaseToMoveInConversion]: {
    label: 'Use lease to move-in conversion in models',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.compsV2]: {
    label: 'Comps v2',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.leaseComplianceReport]: {
    label: 'Lease compliance report',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.compsInsights]: {
    label: 'Comps insights',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.leaseTradeOutReport]: {
    label: 'Lease trade-out report',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.propertyLeads]: {
    label: 'Property leads page',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.automaticRenewalRequests]: {
    label: 'Automatic renewal batches',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.hierarchicalShrinkageConversionModel]: {
    label: 'Hierarchical-shrinkage conversion model',
    description:
      'No longer consulted. Price recommendations always use the hierarchical-shrinkage conversion-model estimator.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.scheduledReportDeliveries]: {
    label: 'Scheduled report deliveries',
    description: 'No longer consulted. Scheduled report deliveries are always available.',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.frigadeAssistant]: {
    label: 'Frigade assistant',
    forceEnabledForDemoFirms: false,
  },
  [Feature.prospectiveProperties]: {
    label: 'Prospective properties',
    forceEnabledForDemoFirms: false,
  },
  [Feature.sso]: {
    label: 'Okta SSO',
    forceEnabledForDemoFirms: false,
  },
  [Feature.leaseUps]: {
    label: 'Lease up',
    description:
      'Enables lease-up mode for properties, including the property state selector and lease-up plans.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.appfolioIntegration]: {
    label: 'AppFolio integration',
    description:
      'Allows creating and configuring an AppFolio integration instance in company settings and admin.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.aimIntegration]: {
    label: 'AIM integration',
    description:
      'Allows creating and configuring an AIM integration instance in company settings and admin.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.applicationConversionAvailabilityForecasting]: {
    label: 'Application conversion in availability forecast',
    description:
      'Discounts a unit’s upcoming lease by how likely its application is to be approved and moved into, instead of treating every application as certain. Affects predicted occupancy and price recommendations, but not leased %.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.eliseaiIntegration]: {
    label: 'EliseAI integration',
    description:
      'Allows creating and configuring an EliseAI integration instance in company settings and admin.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.googleReviews]: {
    label: 'Google reviews',
    description:
      'Syncs Google Maps reviews and AI sentiment for managed properties, and shows them on the overview dashboard, the property summary card, insights, and chat.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.aiRenewalRecommendations]: {
    label: 'AI renewal recommendations',
    description:
      'Shows AI renewal recommendation surfaces (settings, popover, insight card, and activity events). Pilot with one firm. Agent spend is separately gated by the AI renewal recommendations sitevar.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.pmsSpecialsSync]: {
    label: 'PMS specials sync',
    description: 'Allows Entrata properties to opt in to syncing specials from the PMS.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.workrampLaunch]: {
    label: 'Workramp launch',
    description: 'Allows firm users to open the Workramp Academy from Rentana.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.salesFunnelInsights]: {
    label: 'Sales funnel insights',
    forceEnabledForDemoFirms: false,
  },
}
